import React, { useCallback, useState, useEffect } from 'react';
import { Modal, Form, Input } from 'antd';
import { SwitchCommon, ButtonCommon, ErrorMessage, SelectCommon, InputCommon, PrefixedInput } from 'components/common';
import { AttributeFormValues, AddAttributeModalProps, BaseRecord, AttributeType } from 'types';
import { Check, PencilSimple, Plus, Trash, X } from '@phosphor-icons/react';
import { formatPrice } from 'utils';
const { TextArea } = Input;

const AddAttributeModal: React.FC<AddAttributeModalProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  onGenerate,
  form,
  editMode = false,
  attributeData = null,
  isSubmitting = false,
  isCopyMode = false,
}) => {
  const [isActive, setIsActive] = useState<boolean>(true);
  const [modalOpened, setModalOpened] = useState(false);
  const [previousAttributeType, setPreviousAttributeType] = useState<string>('');
  const [confirmedRows, setConfirmedRows] = useState<number[]>([]);
  const [codePrefix, setCodePrefix] = useState<string>('');
  const [genCode, setGenCode] = useState<string>('');
  const handleToggleActive = useCallback(
    (checked: boolean) => {
      setIsActive(checked);
      form.setFieldValue('isActive', checked);
    },
    [form],
  );

  const handleFormValuesChange = useCallback(
    (changedValues: Partial<AttributeFormValues>) => {
      if ('type' in changedValues && !editMode) {
        const newType = changedValues.type;

        if (newType && newType !== previousAttributeType) {
          setPreviousAttributeType(newType);
          onGenerate();
        }
      }
    },
    [editMode, previousAttributeType, onGenerate],
  );
  useEffect(() => {
    if (genCode) {
      const prefix = genCode.substring(0, 7);
      setCodePrefix(prefix);
      form.setFieldsValue({ code: genCode });
    }
    if (attributeData?.code) {
      const prefix = attributeData.code.substring(0, 7);
      setCodePrefix(prefix);
    }
  }, [attributeData?.code, genCode]);
  useEffect(() => {
    const fetchCode = async () => {
      if (isOpen && !modalOpened) {
        setModalOpened(true);

        if (!editMode) {
          const code = await onGenerate();
          setGenCode(code);
        }
      }

      if (!isOpen) {
        setModalOpened(false);
      }
    };
    fetchCode();
  }, [isOpen, editMode, onGenerate, modalOpened]);
  useEffect(() => {
    if (isOpen) {
      if (editMode && attributeData) {
        setIsActive(attributeData.isActive);
        form.setFieldsValue({
          name: attributeData.name,
          code: attributeData.code,
          type: attributeData.type,
          convertValue: attributeData.convertValue,
          isActive: attributeData.isActive,
          description: attributeData.description as string,
        });
      } else if (!editMode && !isCopyMode) {
        form.resetFields();
        setIsActive(true);
        form.setFieldsValue({
          isActive: true,
          type: AttributeType.PRODUCT,
          convertValue: 1,
        });
      }
    }
  }, [isOpen, form, editMode, attributeData, isCopyMode]);

  const HiddenFormItems = () => (
    <Form.Item name='isActive' hidden initialValue={true}>
      <Input />
    </Form.Item>
  );

  const getModalTitle = () => {
    if (editMode) {
      return 'CHỈNH SỬA THUỘC TÍNH';
    }
    return 'TẠO THUỘC TÍNH MỚI';
  };

  const getModalDescription = () => {
    if (editMode) {
      return 'Vui lòng chỉnh sửa thông tin thuộc tính theo nhu cầu của bạn';
    }
    return 'Vui lòng điền đầy đủ thông tin của thuộc tính trước khi thêm mới';
  };

  const getSubmitButtonText = () => {
    if (editMode) {
      return 'Cập nhật';
    }
    return 'Thêm thuộc tính';
  };

  return (
    <Modal
      title={
        <div className='flex flex-col bg-white py-[12px] px-[24px] rounded-lg gap-2'>
          <h2 className='text-[18px] font-bold leading-[24px]'>{getModalTitle()}</h2>
          <p className='text-[14px] leading-[20px] text-[var(--textBlack56)] font-normal'>{getModalDescription()}</p>
        </div>
      }
      open={isOpen}
      // onCancel={onCancel}
      footer={null}
      width={1200}
      centered
      className='attribute-modal'
      closeIcon={null}
      maskClosable={false}
    >
      <div className='px-[24px] py-[12] mb-[12px] max-h-[calc(100vh-200px)] overflow-y-auto'>
        <Form
          form={form}
          requiredMark={false}
          layout='vertical'
          className='bg-white rounded-lg py-[24px] px-[48px] font-medium text-[14px] leading-[20px]'
          onValuesChange={handleFormValuesChange}
        >
          <HiddenFormItems />

          <div className='flex items-start space-x-4'>
            <Form.Item
              label={
                <span className='relative'>
                  Tên thuộc tính
                  <span className='absolute top-0.5 -right-3.5 text-red-500 text-[8px] font-bold'>✱</span>
                </span>
              }
              name='name'
              className='flex-1'
              rules={[
                { required: true, message: <ErrorMessage message='Giá trị này là bắt buộc, bạn phải nhập giá trị!' /> },
                { max: 100, message: <ErrorMessage message='Tên thuộc tính không được vượt quá 100 ký tự!' /> },
              ]}
            >
              <Input placeholder='Nhập vào tên thuộc tính' />
            </Form.Item>
            <Form.Item
              label={
                <span className='relative'>
                  Mã thuộc tính <span className='absolute top-0.5 -right-3.5 text-red-500 text-[8px] font-bold'>✱</span>
                </span>
              }
              name='code'
              className='w-[240px]'
              rules={[
                { required: true, message: <ErrorMessage message='Vui lòng nhập mã thuộc tính!' /> },
                { max: 20, message: <ErrorMessage message='Mã thuộc tính không được vượt quá 20 ký tự!' /> },
                {
                  pattern: /^[a-zA-Z0-9_]+$/,
                  message: <ErrorMessage message='Mã thuộc tính chỉ được chứa chữ cái, số và dấu gạch dưới!' />,
                },
              ]}
            >
              <PrefixedInput
                prefix={codePrefix}
                maxEditableLength={6}
                placeholder='Nhập mã'
                disabled={editMode && !isCopyMode}
                className='!text-[14px] !leading-[20px] h-[32px]'
                inputProps={{
                  suffix: (!editMode || isCopyMode) && (
                    <span
                      className='text-[var(--primaryColor500)] cursor-pointer'
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onGenerate) {
                          try {
                            const code = await onGenerate();
                            if (code && code.length >= 6) {
                              setCodePrefix(code.substring(0, 7));
                              form.setFieldsValue({ code });
                            }
                          } catch (error) {
                            console.error('Error generating code:', error);
                          }
                        }
                      }}
                    >
                      Generate
                    </span>
                  ),
                  hardSize: 'sm',
                }}
              />
            </Form.Item>

            {/* <Form.Item
              label={
                <span className='relative'>
                  Danh mục
                  <span className='absolute top-0.5 -right-3.5 text-red-500 text-[8px] font-bold'>✱</span>
                </span>
              }
              name='type'
              className='w-[240px]'
              rules={[
                { required: true, message: <ErrorMessage message='Giá trị này là bắt buộc, bạn phải chọn giá trị!' /> },
              ]}
            >
              <Select placeholder='Chọn danh mục'>
                <Select.Option value={AttributeType.PRODUCT}>Chai</Select.Option>
                <Select.Option value={AttributeType.UNIT}>Đơn vị</Select.Option>
                <Select.Option value={AttributeType.PRICE}>Giá</Select.Option>
                <Select.Option value={AttributeType.OTHER}>Khác</Select.Option>
              </Select>
            </Form.Item> */}

            {/* <Form.Item
              label={
                <span className='relative'>
                  Giá trị quy đổi
                  <span className='absolute top-0.5 -right-3.5 text-red-500 text-[8px] font-bold'>✱</span>
                </span>
              }
              name='convertValue'
              className='w-[240px]'
              rules={[
                { required: true, message: <ErrorMessage message='Vui lòng nhập giá trị quy đổi!' /> },
                {
                  type: 'number',
                  min: 0.01,
                  message: <ErrorMessage message='Giá trị quy đổi phải lớn hơn 0!' />,
                },
              ]}
            >
              <InputNumber placeholder='Nhập giá trị quy đổi' className='w-full' precision={2} min={0.01} step={0.01} />
            </Form.Item> */}

            <div className='mt-8 flex items-center ml-4'>
              <SwitchCommon
                label={isActive ? 'Kích hoạt' : 'Chưa kích hoạt'}
                isActive={isActive}
                record={{} as BaseRecord<unknown>}
                onToggle={handleToggleActive}
                disabled={editMode}
              />
            </div>
          </div>

          <Form.Item
            className='font-normal'
            label='Mô tả thuộc tính'
            name='description'
            rules={[{ max: 1000, message: <ErrorMessage message='Mô tả không được vượt quá 1000 ký tự!' /> }]}
          >
            <TextArea rows={8} placeholder='Nhập vào mô tả...' className='resize-none' />
          </Form.Item>
          <Form.Item required className='!mb-0 '>
            <Form.List name='materials' initialValue={[['Đinh', 10, `+${formatPrice(12000)} VNĐ`]]}>
              {(fields, { add, remove }) => (
                <div className='flex flex-col gap-2 '>
                  {fields.map((field, idx) => {
                    const values = form.getFieldValue(['materials', field.name]) || [];
                    const isConfirmed = confirmedRows.includes(field.key);

                    return (
                      <div
                        key={idx}
                        className='self-stretch inline-flex justify-start items-center gap-2 bg-[var(--textBlack2)] px-2 py-1 rounded-xl w-full'
                      >
                        {/* Nguyên liệu */}
                        <Form.Item
                          label={
                            <span className='text-[14px] leading-[20px] text-[var(--textBlack56)] font-normal w-[200px]'>
                              Nguyên liệu
                            </span>
                          }
                          name={[field.name, 0]}
                          rules={[{ required: true, message: 'Bắt buộc' }]}
                          className='mb-0'
                        >
                          {isConfirmed ? (
                            <span className='block rounded px-2 py-1 text-[14px] font-medium'>{values[0]}</span>
                          ) : (
                            <SelectCommon
                              className=' bg-white !text-[14px] !leading-[20px] font-medium'
                              options={[{ label: 'Đinh', value: 'Đinh' }]}
                              placeholder='Chọn'
                            />
                          )}
                        </Form.Item>
                        {/* Số lượng */}
                        <Form.Item
                          label={
                            <span className='text-[14px] leading-[20px] text-[var(--textBlack56)] font-normal w-[200px]'>
                              Số lượng
                            </span>
                          }
                          name={[field.name, 1]}
                          rules={[{ required: true, message: 'Bắt buộc' }]}
                          className='mb-0'
                        >
                          {isConfirmed ? (
                            <span className='block rounded px-2 py-1 text-[14px] font-medium'>{values[1]}</span>
                          ) : (
                            <SelectCommon
                              className=' bg-white !text-[14px] !leading-[20px] font-medium'
                              options={[{ label: '10', value: 10 }]}
                              placeholder='Chọn'
                            />
                          )}
                        </Form.Item>
                        {/* Giá thêm */}
                        <Form.Item
                          name={[field.name, 2]}
                          label={
                            <span className='text-[14px] leading-[20px] text-[var(--textBlack56)] font-normal  w-[200px]'>
                              Giá thêm
                            </span>
                          }
                          rules={[{ required: true, message: 'Bắt buộc' }]}
                          className='mb-0'
                        >
                          {isConfirmed ? (
                            <span className='block  rounded px-2 py-1 text-[14px] font-medium'>{values[2]}</span>
                          ) : (
                            <InputCommon
                              disabled
                              value={`+${formatPrice(12000)} VNĐ`}
                              className=' !text-[14px] !leading-[20px] font-medium !text-[var(--textBlack)]'
                              placeholder='Nhập giá thêm'
                            />
                          )}
                        </Form.Item>

                        <div className='flex-1 h-full px-2 flex justify-end items-center gap-2'>
                          {!isConfirmed ? (
                            <>
                              <Check
                                size={20}
                                className='text-green-500 cursor-pointer'
                                onClick={() => setConfirmedRows([...confirmedRows, field.key])}
                              />
                              <X
                                size={18}
                                className='text-rose-500 cursor-pointer'
                                onClick={() => remove(field.name)}
                              />
                            </>
                          ) : (
                            <>
                              <PencilSimple
                                size={18}
                                className='text-black cursor-pointer'
                                onClick={() => setConfirmedRows(confirmedRows.filter((k) => k !== field.key))}
                              />
                              <Trash
                                size={18}
                                className='text-rose-500 cursor-pointer'
                                onClick={() => remove(field.name)}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <button
                    type='button'
                    className='h-8 px-2 py-2.5 max-w-fit bg-black/5 rounded-lg flex items-center gap-2.5 mt-2'
                    onClick={() => add(['Đinh', 10, `+${formatPrice(12000)} VNĐ`])}
                  >
                    <Plus size={20} className='text-black' />
                    <span className='text-black text-sm'>Thêm nguyên liệu</span>
                  </button>
                </div>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </div>
      <div className='flex justify-center gap-3 mt-6'>
        <ButtonCommon color='none' size='md' radius='rounded' onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </ButtonCommon>
        <ButtonCommon
          color='primary'
          size='md'
          radius='rounded'
          loading={isSubmitting}
          onClick={() => form.validateFields().then(onSubmit)}
        >
          {getSubmitButtonText()}
        </ButtonCommon>
      </div>
    </Modal>
  );
};

export default AddAttributeModal;
